/**
 * Web Push subscribe / unsubscribe helpers.
 *
 * The backend at /api/push/* handles the storage. We register the SW once,
 * fetch the VAPID public key, ask for permission, subscribe, and POST the
 * subscription JSON to /api/push/subscribe.
 */
import { api } from "./api";

function urlBase64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window
  );
}

export async function getPushState() {
  if (!pushSupported()) {
    return { supported: false, permission: "denied", subscribed: false };
  }
  const permission = Notification.permission;
  let subscribed = false;
  try {
    const reg = await navigator.serviceWorker.getRegistration("/sw.js");
    if (reg) {
      const sub = await reg.pushManager.getSubscription();
      subscribed = !!sub;
    }
  } catch {
    /* quiet */
  }
  return { supported: true, permission, subscribed };
}

async function _registerSw() {
  if (!pushSupported()) throw new Error("Push not supported on this device.");
  const existing = await navigator.serviceWorker.getRegistration("/sw.js");
  if (existing) return existing;
  return await navigator.serviceWorker.register("/sw.js", { scope: "/" });
}

export async function enablePush() {
  if (!pushSupported())
    throw new Error("Push not supported on this device.");
  const reg = await _registerSw();
  await navigator.serviceWorker.ready;
  // Permission
  let permission = Notification.permission;
  if (permission === "default") permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error(
      "Permission politely declined — nothing changes. You can enable later."
    );
  }
  // Public key
  const cfg = await api.get("/push/public-key");
  if (!cfg.data?.public_key) {
    throw new Error("Push isn't configured on the server yet.");
  }
  const appServerKey = urlBase64ToUint8Array(cfg.data.public_key);
  // Subscribe
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: appServerKey,
    });
  }
  const raw = sub.toJSON();
  const r = await api.post("/push/subscribe", {
    endpoint: raw.endpoint,
    keys: { p256dh: raw.keys.p256dh, auth: raw.keys.auth },
    device_label:
      (navigator.platform || "Device") + " · " + (navigator.userAgent.split(" ").pop() || ""),
  });
  return r.data;
}

export async function disablePush() {
  if (!pushSupported()) return { ok: true };
  const reg = await navigator.serviceWorker.getRegistration("/sw.js");
  if (!reg) return { ok: true };
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    try {
      const raw = sub.toJSON();
      // Try to delete the matching server-side subscription for cleanliness.
      const list = await api.get("/push/subscriptions");
      const match = (list.data || []).find((s) => s.endpoint === raw.endpoint);
      if (match) await api.delete(`/push/subscribe/${match.id}`);
    } catch {
      /* quiet — local unsubscribe still proceeds */
    }
    await sub.unsubscribe();
  }
  return { ok: true };
}
